/**
 * ProductRegistryCockpit — Cockpit-language frame for /product-registry.
 *
 * Wraps ProductRegistry with a Cockpit Masthead + KPI strip to guarantee
 * visible cockpit chrome. The interactive registry content is preserved.
 */
import { useMemo } from "react";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider, CockpitKpiStrip,
} from "@/components/cockpit/CockpitShell";
import { useProductRegistry } from "@/data/ProductRegistryContext";
import ProductRegistry from "./ProductRegistry";

export default function ProductRegistryCockpit() {
  const { products } = useProductRegistry();

  const stats = useMemo(() => {
    const total = products.length;
    const active = products.filter((p) => p.active).length;
    const lowStock = products.filter((p) =>
      p.trackInventory && p.stockQuantity <= p.reorderThreshold,
    ).length;
    const categories = new Set(products.map((p) => p.category).filter(Boolean)).size;
    return { total, active, lowStock, categories };
  }, [products]);

  return (
    <CockpitPage>
      <CockpitMasthead
        eyebrow="Catalog · Product registry"
        title="Product Registry"
        right={(
          <>
            <HeroMetric label="Active SKUs" value={stats.active} hero />
            <HeroDivider />
            <HeroMetric label="Total" value={stats.total} />
            <HeroMetric
              label="Low stock"
              value={stats.lowStock}
              tone={stats.lowStock > 0 ? "warn" : "good"}
            />
          </>
        )}
      />
      <CockpitKpiStrip
        items={[
          { label: "Categories", value: stats.categories },
          { label: "Total SKUs", value: stats.total },
          { label: "Active", value: stats.active, tone: "good" },
          { label: "Low stock", value: stats.lowStock, tone: stats.lowStock > 0 ? "warn" : "default" },
        ]}
      />
      <div className="cockpit-surface -mx-6">
        <div className="px-6">
          <ProductRegistry />
        </div>
      </div>
    </CockpitPage>
  );
}
